/**
 * PURPOSE: Actions that trigger push to darwin will be processed by this middleware.
 */

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';

// actions
import * as offerActionCreators from '../actions/offerActionCreators';
import * as pushToDarwinActionCreators from '../actions/pushToDarwinActionCreators';

// consts/enums
import { LINKS_SUCCESS, OFFER_SAVE_NOCHANGE_SUCCESS, OFFER_SAVE_SUCCESS, SOFT_SAVE_SUCCESS } from '../actionTypes/offerActionTypes';
import {
    OPEN_IN_DARWIN,
    OPEN_IN_DARWIN_URL,
    PUSH_TO_DARWIN,
    PUSH_TO_DARWIN_FAILURE,
    PUSH_TO_DARWIN_SUCCESS
} from '../actionTypes/pushToDarwinActionTypes';
import { SOCKET_EVENT_RECEIVED } from '../actionTypes/socketActionTypes';
import { DARWIN_ERROR_MESSAGES, OPEN_IN_DARWIN_FLOW, PUSH_TO_DARWIN_FLOW } from '../constants';

// interfaces/types
import type { ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';

// actions
import { BuildPayloadPurpose } from '@makemydeal/dr-dash-bff-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { thunkEcommerceCancellationWithSave } from '../actions/orderActionCreators';
import * as offerInfoSelectors from '../selectors/offerInfoSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as pushToDarwinSelectors from '../selectors/pushToDarwinSelectors';
import * as vehicleSelectors from '../selectors/vehicleSelectors';
import { buildOfferSavePayloadObject } from '../utils/offerPayloadUtil';
import { isDealVersionFromDarwin } from '../utils/darwinEventUtils';

export const middleware = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);
    const state = store.getState();

    switch (action.type) {
        case OPEN_IN_DARWIN:
        case PUSH_TO_DARWIN: {
            const source = action.type === OPEN_IN_DARWIN ? OPEN_IN_DARWIN_FLOW : PUSH_TO_DARWIN_FLOW;
            const isEcommercePendingCancellation = offerInfoSelectors.getEcommercePendingCancellationStatus(state);
            const purpose = BuildPayloadPurpose.PushToDarwin;

            if (isEcommercePendingCancellation) {
                const meta = {
                    ...action.meta,
                    source
                };
                store.dispatch(thunkEcommerceCancellationWithSave({ purpose }, meta));
            } else {
                const offerSavePayload = buildOfferSavePayloadObject(state, purpose);
                store.dispatch(offerActionCreators.saveOfferRSAA({ state, offerSavePayload, source }));
            }
            break;
        }
        case LINKS_SUCCESS:
        case SOFT_SAVE_SUCCESS:
        case OFFER_SAVE_NOCHANGE_SUCCESS:
        case OFFER_SAVE_SUCCESS: {
            const offerSaveSource = action.meta?.source;
            if (offerSaveSource === PUSH_TO_DARWIN_FLOW || offerSaveSource === OPEN_IN_DARWIN_FLOW) {
                const vehicleStockNumber = vehicleSelectors.getVehicleStockNumber(state);
                store.dispatch(
                    vehicleStockNumber
                        ? pushToDarwinActionCreators.pushToDarwinRSAA(MANAGER_VIEW_APP_PREFIX, offerSaveSource)
                        : pushToDarwinActionCreators.pushToDarwinError({
                              statusCode: 400,
                              errorDetails: DARWIN_ERROR_MESSAGES.STOCK_NUMBER_ERROR
                          })
                );
            }
            break;
        }
        case PUSH_TO_DARWIN_SUCCESS: {
            const offerSaveSource = action.meta?.source;
            if (offerSaveSource === OPEN_IN_DARWIN_FLOW) {
                store.dispatch(pushToDarwinActionCreators.openInDarwinUrl());
            }
            break;
        }
        case OPEN_IN_DARWIN_URL: {
            const url = pushToDarwinSelectors.getDarwinResponseData(state).url;
            if (url && url !== '') {
                window.open(url, '_blank');
            }
            break;
        }

        case PUSH_TO_DARWIN_FAILURE:
            store.dispatch(
                pushToDarwinActionCreators.pushToDarwinError({
                    statusCode: action.payload?.statusCode,
                    errorDetails: action.payload?.message
                })
            );
            break;

        case SOCKET_EVENT_RECEIVED: {
            const event = action.payload;

            const currentDealXGVersion = offerReduxSelectors.getDealXgVersion(state); // if it exists in this reducer's state
            const newDealXGVersion = event.data?.message?.version;

            const isVersionCreatedByDarwin = isDealVersionFromDarwin(event);

            if (isVersionCreatedByDarwin && newDealXGVersion && currentDealXGVersion !== newDealXGVersion) {
                store.dispatch(pushToDarwinActionCreators.newDealVersionCreatedByDarwin());
            }
            break;
        }
    }
};
